import config from "@app/lib/api/config";
import type { RegionInfo } from "@app/types/region";
import { z } from "zod";
import type {
  OAuthConnectionType,
  OAuthCredentials,
  OAuthProvider,
  OAuthUseCase,
} from "../../oauth/lib";
import { isOAuthConnectionType } from "../../oauth/lib";
import { isDevelopment } from "../../shared/env";
import type { Result } from "../../shared/result";
import { Err, Ok } from "../../shared/result";
import type { LightWorkspaceType } from "../../user";

// The sign-in window also leaves its outcome in localStorage. Messages reach
// only listeners alive when it finishes; a tab that reloaded mid sign-in has
// none, but can read this back (or hear it through a `storage` event).
const oauthFinalizedStorageKey = "oauth_finalized";

const OAuthFinalizedRecordSchema = z.object({
  provider: z.string(),
  connectionId: z.string().nullable(),
  error: z.string().nullable(),
  atMs: z.number(),
});

export type OAuthFinalizedRecord = z.infer<typeof OAuthFinalizedRecordSchema>;

function parseOAuthFinalized(raw: string | null): OAuthFinalizedRecord | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = OAuthFinalizedRecordSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function recordOAuthFinalized(
  record: Omit<OAuthFinalizedRecord, "atMs">
): void {
  try {
    window.localStorage.setItem(
      oauthFinalizedStorageKey,
      JSON.stringify({ ...record, atMs: Date.now() })
    );
  } catch {
    // Storage blocked: only a reload mid sign-in loses the outcome.
  }
}

export function readOAuthFinalized(): OAuthFinalizedRecord | null {
  try {
    return parseOAuthFinalized(
      window.localStorage.getItem(oauthFinalizedStorageKey)
    );
  } catch {
    return null;
  }
}

export function onOAuthFinalized(
  listener: (record: OAuthFinalizedRecord) => void
): () => void {
  const handleStorage = (event: StorageEvent) => {
    const record =
      event.key === oauthFinalizedStorageKey
        ? parseOAuthFinalized(event.newValue)
        : null;
    if (record) {
      listener(record);
    }
  };
  window.addEventListener("storage", handleStorage);
  return () => window.removeEventListener("storage", handleStorage);
}

// Opens the sign-in window straight from a click, before any request: opened
// after a slow one, browsers block it and the click seems to do nothing. Hand
// it to `setupOAuthConnection` once the sign-in page is known.
export function openSignInWindow(): Window | null {
  const signInWindow = window.open("", "_blank");
  if (signInWindow) {
    signInWindow.document.title = "Signing in…";
    signInWindow.document.body.textContent = "Opening the sign-in page…";
    signInWindow.document.body.style.font = "16px system-ui, sans-serif";
    signInWindow.document.body.style.padding = "24px";
  }
  return signInWindow;
}

export async function setupOAuthConnection({
  owner,
  provider,
  useCase,
  extraConfig,
  regionInfo,
  signInWindow,
}: {
  owner: LightWorkspaceType;
  provider: OAuthProvider;
  useCase: OAuthUseCase;
  extraConfig: OAuthCredentials;
  regionInfo: RegionInfo | null;
  // From `openSignInWindow`; without one, a window is opened here.
  signInWindow?: Window | null;
}): Promise<Result<OAuthConnectionType, Error>> {
  return new Promise((resolve) => {
    const oauthBaseUrl = config.getAppUrl();
    // Pass opener origin through OAuth flow so finalize page can postMessage back
    const openerOrigin = window.location.origin;
    let url = `${oauthBaseUrl}/w/${owner.sId}/oauth/${provider}/setup?useCase=${useCase}&openerOrigin=${encodeURIComponent(openerOrigin)}`;
    if (extraConfig) {
      url += `&extraConfig=${encodeURIComponent(JSON.stringify(extraConfig))}`;
    }
    // Pass region so the OAuth popup's RegionContext initializes with the correct API URL.
    if (regionInfo) {
      url += `&region=${encodeURIComponent(regionInfo.name)}`;
    }
    if (signInWindow && !signInWindow.closed) {
      signInWindow.location.href = url;
    }
    const oauthPopup =
      signInWindow && !signInWindow.closed ? signInWindow : window.open(url);
    if (!oauthPopup) {
      resolve(
        new Err(
          new Error(
            "Your browser blocked the sign-in window. Allow pop-ups for this site, then try again."
          )
        )
      );
      return;
    }
    let authComplete = false;

    const handleFinalization = (data: any) => {
      if (authComplete) {
        return; // Already processed
      }

      if (data.type === "connection_finalized" && data.provider === provider) {
        authComplete = true;
        const { error, connection } = data;

        cleanup();
        oauthPopup.close();

        if (error) {
          resolve(new Err(new Error(error)));
        } else if (
          connection &&
          isOAuthConnectionType(connection) &&
          connection.provider === provider
        ) {
          resolve(new Ok(connection));
        } else {
          resolve(
            new Err(
              new Error("Invalid connection data received from auth window")
            )
          );
        }
      }
    };

    // Method 1: window.postMessage (preferred, direct communication)
    // The finalize page runs on the app (SPA), same origin as the opener.
    // In dev, bypass origin check as an extra safeguard for cross-port communication.
    const expectedOrigin = new URL(oauthBaseUrl).origin;
    const handleWindowMessage = (event: MessageEvent) => {
      if (!isDevelopment() && event.origin !== expectedOrigin) {
        return;
      }
      handleFinalization(event.data);
    };

    window.addEventListener("message", handleWindowMessage);

    // Method 2: BroadcastChannel (fallback)
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel("oauth_finalize");
      channel.addEventListener("message", (event: MessageEvent) => {
        handleFinalization(event.data);
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      // biome-ignore lint/correctness/noUnusedVariables: ignored using `--suppress`
    } catch (e) {
      // BroadcastChannel not supported
    }

    const cleanup = () => {
      window.removeEventListener("message", handleWindowMessage);
      if (channel) {
        channel.close();
      }
    };
  });
}
